// ─────────────────────────────────────────────────────────────────────────────
// rexyConsole — JARVIS homepage console palette (Chunk 5, desktop).
//
// Mirrors the iOS REXY_CONSOLE values byte-for-byte
// (mobile/rexy-ios/src/lib/theme.ts) and the v3 mockup. This is the console
// palette (#0A0A1A / #00BFFF / gold) — DISTINCT from the existing rexy-ui chat
// tailwind `rexy.*` palette, and scoped to the homepage only. Same values as iOS.
// ─────────────────────────────────────────────────────────────────────────────

export const REXY_CONSOLE = {
  bg: '#0A0A1A',
  panel: '#0D0D24',
  cardBg: 'rgba(8,8,24,0.55)',
  cardBorder: 'rgba(0,191,255,0.20)',
  primary: '#00BFFF',
  secondary: '#FFFFFF',
  warning: '#FFD700',
  critical: '#EF0107',
  gold: '#FFD700',
  grid: 'rgba(0,191,255,0.05)',
  gridStep: 40,
  dimText: 'rgba(0,191,255,0.32)',
  mutedText: 'rgba(0,191,255,0.45)',
  divider: 'rgba(0,191,255,0.12)',
  sectionBorder: 'rgba(0,191,255,0.20)',
  // Telemetry badge overlays + low-opacity surfaces — mirror iOS theme.ts REXY_CONSOLE.
  badgeOkBg: 'rgba(0,191,255,0.12)',
  badgeInfBg: 'rgba(255,255,255,0.04)',
  badgeWrnBg: 'rgba(255,215,0,0.08)',
  badgeErrBg: 'rgba(239,1,7,0.08)',
  badgeSecBg: 'rgba(239,1,7,0.15)',
  infText: 'rgba(255,255,255,0.65)',
  rowDivider: 'rgba(0,191,255,0.04)',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// REXY_FONT — the single OS-wide type stack (owner ruling 2026-07-24).
//
// SYSTEM-WIDE: every surface (Dashboard panels, right column, Chat, Console, the
// CODE workspace) renders in this humanist sans. The prior 'Courier New' monospace
// read as harsh for prose, and most of the screen is now prose (her notes, reports,
// dreamstate). Softer target: open apertures, generous letter-spacing, comfortable
// line-height (see index.css body rules).
//
// SYSTEM STACK ONLY — no webfont, no external fetch, no new dependency. Each surface
// still declares `const MONO = REXY_FONT` locally; the constant NAME is retained
// deliberately to avoid a ~200-usage rename churn across 29 files. It is no longer
// monospace — read it as "the OS type token".
//
// ALIGNMENT: column alignment is flex/gap-based throughout (no char-width padding),
// so proportional type does not disturb it. Any surface that genuinely needs
// tabular alignment should use `fontVariantNumeric: 'tabular-nums'`, never a
// monospace exception.
// ─────────────────────────────────────────────────────────────────────────────
export const REXY_FONT =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

// Orb particle palette (teal ↔ gold) — identical to iOS RexyOrb STAR_COOL/STAR_GOLD.
export const STAR_COOL: [number, number, number] = [0, 210, 200];
export const STAR_GOLD: [number, number, number] = [255, 200, 80];
